import type { SupportedLocale } from './i18n/index.ts';

export interface DictationCopy {
  speak: string;
  consent: string;
  start: string;
  cancel: string;
  listening: string;
  stop: string;
  processing: string;
  review: string;
  use: string;
  undo: string;
  error: string;
  overflow: string;
  undoChanged: string;
  empty: string;
  added: string;
}

// Kept separate from attachment-recording copy: draft audio is never an attachment.
const copies: Record<SupportedLocale, DictationCopy> = {
  en: {
    speak: 'Speak',
    consent:
      'Record up to 60 seconds. When you stop, audio is sent to OpenAI for transcription and text is added here. OnCatch does not save draft audio; OpenAI may retain it under its API data policy. Press Escape before stopping to discard without transfer.',
    start: 'Waiting for microphone permission…',
    cancel: 'Cancel',
    listening: 'Microphone on · recording',
    stop: 'Stop speaking',
    processing: 'Transcribing… Your microphone is off.',
    review: 'Review your words',
    use: 'Use text',
    undo: 'Undo voice text',
    error: 'Dictation is unavailable. Check microphone access or try again. You can keep typing.',
    overflow: 'Voice text exceeds this field’s limit. Try a shorter recording.',
    undoChanged:
      'You have edited this field. Your changes were kept; remove the voice text manually.',
    empty: 'No speech was detected. Try again or keep typing.',
    added: 'Voice text added. Edit anything before sending.',
  },
  fr: {
    speak: 'Dicter',
    consent:
      'Enregistrez jusqu’à 60 secondes. À l’arrêt, l’audio est envoyé à OpenAI pour transcription et le texte est ajouté ici. OnCatch ne conserve pas le brouillon audio ; OpenAI peut le conserver selon sa politique API. Appuyez sur Échap avant l’arrêt pour supprimer sans transfert.',
    start: 'En attente de l’autorisation du micro…',
    cancel: 'Annuler',
    listening: 'Micro actif · enregistrement',
    stop: 'Arrêter',
    processing: 'Transcription… Le micro est éteint.',
    review: 'Relisez vos mots',
    use: 'Utiliser le texte',
    undo: 'Annuler le texte dicté',
    error: 'Dictée indisponible. Vérifiez le micro ou réessayez. Vous pouvez saisir le texte.',
    overflow: 'Le texte dicté dépasse la limite de ce champ. Essayez un enregistrement plus court.',
    undoChanged:
      'Vous avez modifié ce champ. Vos changements sont conservés ; supprimez le texte dicté manuellement.',
    empty: 'Aucune parole détectée. Réessayez ou saisissez le texte.',
    added: 'Texte dicté ajouté. Vous pouvez le modifier avant l’envoi.',
  },
  de: {
    speak: 'Sprechen',
    consent:
      'Nehmen Sie bis zu 60 Sekunden auf. Beim Stoppen wird Audio zur Transkription an OpenAI gesendet und der Text hier eingefügt. OnCatch speichert den Audioentwurf nicht; OpenAI kann ihn gemäß API-Richtlinie speichern. Drücken Sie vor dem Stoppen Escape, um ohne Übertragung abzubrechen.',
    start: 'Warten auf Mikrofonberechtigung…',
    cancel: 'Abbrechen',
    listening: 'Mikrofon an · Aufnahme',
    stop: 'Aufnahme stoppen',
    processing: 'Transkription… Mikrofon aus.',
    review: 'Text prüfen',
    use: 'Text verwenden',
    undo: 'Sprachtext rückgängig',
    error: 'Diktat nicht verfügbar. Mikrofon prüfen oder erneut versuchen. Tippen bleibt möglich.',
    overflow:
      'Der diktierte Text überschreitet das Feldlimit. Versuchen Sie eine kürzere Aufnahme.',
    undoChanged:
      'Dieses Feld wurde bearbeitet. Änderungen bleiben erhalten; Sprachtext bitte manuell entfernen.',
    empty: 'Keine Sprache erkannt. Erneut versuchen oder tippen.',
    added: 'Sprachtext hinzugefügt. Vor dem Senden bearbeitbar.',
  },
  es: {
    speak: 'Dictar',
    consent:
      'Graba hasta 60 segundos. Al detener, el audio se envía a OpenAI para transcripción y el texto se añade aquí. OnCatch no guarda el borrador de audio; OpenAI puede conservarlo según su política de API. Pulsa Escape antes de detener para descartar sin transferir.',
    start: 'Esperando permiso del micrófono…',
    cancel: 'Cancelar',
    listening: 'Micrófono activo · grabando',
    stop: 'Dejar de hablar',
    processing: 'Transcribiendo… El micrófono está apagado.',
    review: 'Revisa tus palabras',
    use: 'Usar texto',
    undo: 'Deshacer texto dictado',
    error: 'Dictado no disponible. Comprueba el micrófono o reintenta. Puedes seguir escribiendo.',
    overflow: 'El texto dictado supera el límite de este campo. Prueba una grabación más corta.',
    undoChanged:
      'Has editado este campo. Se conservaron los cambios; elimina el texto dictado manualmente.',
    empty: 'No se detectó voz. Reintenta o escribe.',
    added: 'Texto dictado añadido. Puedes editarlo antes de enviar.',
  },
  it: {
    speak: 'Detta',
    consent:
      'Registra fino a 60 secondi. All’arresto, l’audio viene inviato a OpenAI per la trascrizione e il testo viene aggiunto qui. OnCatch non salva la bozza audio; OpenAI può conservarla secondo la politica API. Premi Escape prima dell’arresto per scartare senza trasferire.',
    start: 'In attesa del permesso del microfono…',
    cancel: 'Annulla',
    listening: 'Microfono acceso · registrazione',
    stop: 'Interrompi',
    processing: 'Trascrizione… Microfono spento.',
    review: 'Rivedi le tue parole',
    use: 'Usa testo',
    undo: 'Annulla testo dettato',
    error:
      'Dettatura non disponibile. Controlla il microfono o riprova. Puoi continuare a scrivere.',
    overflow:
      'Il testo dettato supera il limite di questo campo. Prova una registrazione più breve.',
    undoChanged:
      'Hai modificato questo campo. Modifiche conservate; rimuovi il testo dettato manualmente.',
    empty: 'Nessuna voce rilevata. Riprova o scrivi.',
    added: 'Testo dettato aggiunto. Puoi modificarlo prima dell’invio.',
  },
  pt: {
    speak: 'Ditar',
    consent:
      'Grave até 60 segundos. Ao parar, o áudio é enviado à OpenAI para transcrição e o texto é adicionado aqui. A OnCatch não salva o rascunho de áudio; a OpenAI pode retê-lo conforme sua política de API. Pressione Escape antes de parar para descartar sem transferir.',
    start: 'Aguardando permissão do microfone…',
    cancel: 'Cancelar',
    listening: 'Microfone ligado · gravando',
    stop: 'Parar de falar',
    processing: 'Transcrevendo… Microfone desligado.',
    review: 'Revise suas palavras',
    use: 'Usar texto',
    undo: 'Desfazer texto ditado',
    error: 'Ditado indisponível. Verifique o microfone ou tente novamente. Você pode digitar.',
    overflow: 'O texto ditado excede o limite deste campo. Tente uma gravação mais curta.',
    undoChanged: 'Você editou este campo. Alterações mantidas; remova o texto ditado manualmente.',
    empty: 'Nenhuma fala detectada. Tente novamente ou digite.',
    added: 'Texto ditado adicionado. Edite antes de enviar.',
  },
  nl: {
    speak: 'Spreken',
    consent:
      'Neem maximaal 60 seconden op. Bij stoppen wordt audio naar OpenAI gestuurd voor transcriptie en wordt de tekst hier toegevoegd. OnCatch bewaart de conceptaudio niet; OpenAI kan deze volgens het API-beleid bewaren. Druk vóór stoppen op Escape om zonder overdracht te annuleren.',
    start: 'Wachten op microfoontoestemming…',
    cancel: 'Annuleren',
    listening: 'Microfoon aan · opnemen',
    stop: 'Stoppen',
    processing: 'Transcriberen… Microfoon uit.',
    review: 'Controleer je woorden',
    use: 'Tekst gebruiken',
    undo: 'Gesproken tekst ongedaan maken',
    error:
      'Dicteren niet beschikbaar. Controleer de microfoon of probeer opnieuw. Typen blijft mogelijk.',
    overflow: 'De gesproken tekst overschrijdt de veldlimiet. Probeer een kortere opname.',
    undoChanged:
      'Dit veld is bewerkt. Wijzigingen blijven behouden; verwijder de gesproken tekst handmatig.',
    empty: 'Geen spraak gedetecteerd. Probeer opnieuw of typ.',
    added: 'Gesproken tekst toegevoegd. Bewerk vóór verzending.',
  },
};

export function dictationCopy(locale: SupportedLocale): DictationCopy {
  return copies[locale];
}
